import React from 'react';
import styled from 'styled-components';

import { isNotNullish } from '@tager/web-core';

import DetailedCarFullSpecification from '@/components/DetailedCarFullSpecification';
import { useTypedSelector } from '@/store/store';
import { selectDetailedCarData } from '@/store/reducers/detailed-car';
import { switchNamesItems } from '@/modules/DetailedCar/DetailedCar.helper';
import {
  CodeItems,
  DetailedCarFullSpecificationItem,
} from '@/components/DetailedCarFullSpecification/DetailedCarFullSpecification.types';

function FullSpecification() {
  const data = useTypedSelector(selectDetailedCarData);

  if (!data) {
    return null;
  }

  const {
    warranty,
    content: { equipment, additional },
    params: {
      body,
      fuel,
      wheel,
      enginePower,
      engineSecondPower,
      maxSpeed,
      time0To100,
      seatsCount,
      electricEnginesCount,
      engineMaxTorque,
      engineSecondMaxTorque,
      engineCapacity,
      wltpExpense,
      doorsCount,
      length,
      width,
      height,
      clearance,
      trunkVolume,
      maxTrunkVolume,
      wheelBase,
      rotateDiameter,
      trackFront,
      trackBack,
      gearbox,
      gearboxCount,
      weight,
      weightFull,
      battery,
      batteryCapacity,
      chargeDcTime,
      chargeAcTime,
      distanceLimit,
    },
  } = data;

  const specificationItems: DetailedCarFullSpecificationItem[] = [
    {
      name: 'Двигатель',
      code: CodeItems.ENGINE,
      items: [
        {
          options: [
            fuel?.label
              ? { name: 'Тип двигателя', value: fuel.label }
              : undefined,
            electricEnginesCount
              ? {
                  name: 'Количество электродвигателей',
                  value: String(electricEnginesCount),
                }
              : undefined,
            engineMaxTorque
              ? {
                  name: 'Максимальный крутящий момент',
                  value:
                    engineMaxTorque +
                    ' нм' +
                    (engineSecondMaxTorque
                      ? '/' + engineSecondMaxTorque + ' нм'
                      : ''),
                }
              : undefined,
            wheel?.label ? { name: 'Привод', value: wheel.label } : undefined,
            enginePower
              ? {
                  name: 'Мощность двигателя',
                  value:
                    enginePower +
                    ' л.c' +
                    (engineSecondPower ? '/' + engineSecondPower + ' л.c' : ''),
                }
              : undefined,
            engineCapacity
              ? { name: 'Объем двигателя', value: engineCapacity + ' л' }
              : undefined,
            wltpExpense
              ? { name: 'WLTP Расход до', value: wltpExpense + ' кВтч/100 км' }
              : undefined,
            maxSpeed
              ? { name: 'Максимальная скорость', value: maxSpeed + ' км/ч' }
              : undefined,
            time0To100
              ? {
                  name: 'Время разгона до 100 км/ч',
                  value: time0To100 + ' сек',
                }
              : undefined,
          ].filter(isNotNullish),
        },
      ].filter((item) => item.options.length !== 0),
      isOpened: true,
    },
    {
      name: 'Габариты авто',
      code: CodeItems.BODY,
      items: [
        {
          options: [
            body?.label
              ? { name: 'Тип кузова', value: body?.label }
              : undefined,
            doorsCount
              ? { name: 'Количество дверей', value: String(doorsCount) }
              : undefined,
            seatsCount
              ? { name: 'Количество мест', value: String(seatsCount) }
              : undefined,
            length ? { name: 'Длина', value: length + ' мм' } : undefined,
            width ? { name: 'Ширина', value: width + ' мм' } : undefined,
            height ? { name: 'Высота', value: height + ' мм' } : undefined,
            clearance
              ? { name: 'Клиренс', value: clearance + ' мм' }
              : undefined,
            trunkVolume
              ? { name: 'Объем багажника', value: trunkVolume + ' л' }
              : undefined,
            maxTrunkVolume
              ? {
                  name: 'Максимальный объем багажника',
                  value: maxTrunkVolume + ' л',
                }
              : undefined,
            wheelBase
              ? { name: 'Колесная база', value: wheelBase + ' мм' }
              : undefined,
            rotateDiameter
              ? { name: 'Диаметр разворота', value: rotateDiameter + ' м' }
              : undefined,
            trackFront
              ? { name: 'Колея передняя', value: trackFront + ' мм' }
              : undefined,
            trackBack
              ? { name: 'Колея задняя', value: trackBack + ' мм' }
              : undefined,
          ].filter(isNotNullish),
        },
      ].filter((item) => item.options.length !== 0),
      isOpened: true,
    },
    {
      name: 'Эксплуатационные характеристики',
      code: CodeItems.OPERATION_PARAMS,
      items: [
        {
          options: [
            gearbox?.label
              ? { name: 'Коробка передач', value: gearbox.label }
              : undefined,
            gearboxCount
              ? { name: 'Количество передач', value: String(gearboxCount) }
              : undefined,
            weight
              ? { name: 'Снаряженная масса', value: weight + ' кг' }
              : undefined,
            weightFull
              ? { name: 'Полная масса', value: weightFull + ' кг' }
              : undefined,
            warranty?.text
              ? { name: 'Гарантия', value: warranty.text }
              : undefined,
          ].filter(isNotNullish),
        },
      ].filter((item) => item.options.length !== 0),
      isOpened: true,
    },
    {
      name: 'Батарея',
      code: CodeItems.BATTERY,
      items: [
        {
          options: [
            battery?.label
              ? { name: 'Тип батареи', value: battery?.label }
              : undefined,
            batteryCapacity
              ? { name: 'Емкость батареи', value: batteryCapacity + ' кВт*ч' }
              : undefined,
            chargeDcTime
              ? {
                  name: 'Время зарядки постоянным током (DC)',
                  value: chargeDcTime + ' ч',
                }
              : undefined,
            chargeAcTime
              ? {
                  name: 'Время зарядки переменным током (AC)',
                  value: chargeAcTime + ' ч',
                }
              : undefined,
            distanceLimit
              ? { name: 'Запас хода', value: distanceLimit + ' км' }
              : undefined,
          ].filter(isNotNullish),
        },
      ].filter((item) => item.options.length !== 0),
      isOpened: true,
    },
    equipment && equipment.length > 0
      ? {
          name: 'Заводские опции',
          code: CodeItems.COMPLECTATION,
          items: equipment.map((item) => ({
            section: item.section ?? '',
            options:
              item.options && item.options.length > 0
                ? item.options.map((option) => ({
                    name: option.name ?? '',
                    value: option.value ?? '',
                  }))
                : [],
          })),
        }
      : undefined,
    additional.length > 0
      ? {
          name: 'Преимущества',
          code: CodeItems.LIKE,
          items: [{ options: additional.map((item) => ({ name: item })) }],
        }
      : undefined,
  ]
    .filter(isNotNullish)
    .filter((item) => item.items.length !== 0);

  if (!specificationItems || specificationItems.length === 0) {
    return null;
  }

  return (
    <Component>
      <DetailedCarFullSpecification array={specificationItems} />
    </Component>
  );
}

export default FullSpecification;

const Component = styled.div`
  margin-top: 40px;
`;
